<template>
    <div class="inset-0 flex flex-col bg-gray-50">
        <div class="bg-white border-b border-gray-200 px-6 py-4">
            <div class="flex items-center justify-between">
                <div class="flex items-center">
                    <router-link
                        :to="{ name: 'editVenue', params: { id: venueId } }"
                        class="mr-4 text-gray-500 hover:text-gray-700"
                    >
                        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </router-link>
                    <h1 class="text-lg font-semibold text-gray-900">Translation Overrides</h1>
                </div>
                <div class="flex items-center space-x-4">
                    <select
                        v-model="selectedLanguage"
                        @change="handleLanguageChange"
                        class="form-select rounded-md border-gray-300 py-1"
                    >
                        <option v-for="lang in languages" :key="lang" :value="lang">
                            {{ lang.toUpperCase() }}
                        </option>
                    </select>
                    <button
                        @click="saveOverrides"
                        :disabled="isLoading"
                        class="bg-indigo-600 text-white px-4 py-1 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {{ isLoading ? 'Saving...' : 'Save Changes' }}
                    </button>
                </div>
            </div>
        </div>

        <div class="flex-1 overflow-hidden">
            <div class="h-full flex flex-col">
                <div class="bg-gray-100 border-b border-gray-200">
                    <div class="grid grid-cols-2 gap-0">
                        <div class="px-6 py-3 border-r border-gray-200">
                            <h2 class="text-sm font-medium text-gray-900">Default Translations</h2>
                            <p class="text-xs text-gray-600">Read-only view of default translation values</p>
                        </div>
                        <div class="px-6 py-3">
                            <h2 class="text-sm font-medium text-gray-900">Translation Overrides</h2>
                            <p class="text-xs text-gray-600">Edit values to override default translations</p>
                        </div>
                    </div>
                </div>
                
                <div class="flex-1 overflow-auto">
                    <div v-if="defaultTranslations" class="p-6">
                        <div class="space-y-4">
                            <div
                                v-for="(value, key) in flattenedDefaults"
                                :key="key"
                                class="grid grid-cols-2 gap-6"
                            >
                                <div class="border border-gray-200 rounded-md p-2">
                                    <label class="block text-xs font-medium text-gray-700 mb-1">
                                        {{ key }}
                                    </label>
                                    <div class="text-xs text-gray-600 bg-gray-50 p-1 rounded  flex items-center">
                                        {{ value }}
                                    </div>
                                </div>
                                
                                <div class="border border-gray-200 rounded-md p-2">
                                    <label class="block text-xs font-medium text-gray-700 mb-1">
                                        {{ key }}
                                    </label>
                                    <textarea
                                        v-model="overrides[key]"
                                        :placeholder="value"
                                        class="form-textarea text-xs w-full rounded-md border-gray-300"
                                        rows="1"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="isLoadingDefaults" class="text-center py-8">
                        <div class="spinner h-6 w-6 mx-auto"></div>
                        <p class="text-gray-600 mt-2">Loading default translations...</p>
                    </div>
                    <div v-else class="text-center py-8 text-gray-500">
                        Failed to load default translations
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapGetters} from "vuex";

export default {
    name: 'TranslationOverrides',
    props: {
        venueId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            selectedLanguage: 'en-GB',
            languages: ['en-GB', 'es', 'fr', 'nl', 'de'],
            defaultTranslations: null,
            overrides: {},
            isLoading: false,
            isLoadingDefaults: false
        };
    },
    computed: {
      ...mapGetters({
          accountId: 'user/accountId',
        }),
        flattenedDefaults() {
            if (!this.defaultTranslations) return {};
            return this.flattenObject(this.defaultTranslations);
        }
    },
    async mounted() {
        await this.loadDefaultTranslations();
        await this.loadTranslations();
    },
    methods: {
        async loadDefaultTranslations() {
            this.isLoadingDefaults = true;
            try {
                const response = await fetch('https://order.storekit.com/en-GB.json');
                this.defaultTranslations = await response.json();
            } catch (error) {
                throw new Error('Failed to load default translations');
            } finally {
                this.isLoadingDefaults = false;
            }
        },
        async handleLanguageChange() {
            await this.loadDefaultTranslations();
            await this.loadTranslations();
        },
        async loadTranslations() {
            try {
                const { data } = await this.$axios.get(
                    `/venues/${this.venueId}/translations/${this.selectedLanguage}?accountId=${this.accountId}`
                );
                this.overrides = this.flattenObject(data.strings || {});
            } catch (error) {
                this.overrides = {};
            }
        },
        async saveOverrides() {
            this.isLoading = true;
            try {
                const unflattenedOverrides = this.unflattenObject(this.overrides);
                await this.$axios.put(
                    `/venues/${this.venueId}/translations/${this.selectedLanguage}`,
                    {
                        strings: unflattenedOverrides
                    }
                );
                this.$notify({
                    group: 'settings',
                    title: 'Translation overrides saved successfully'
                });
            } catch (error) {
                this.$notify({
                    group: 'settings',
                    title: 'Failed to save translation overrides',
                    type: 'error'
                });
            } finally {
                this.isLoading = false;
            }
        },
        flattenObject(obj, prefix = '') {
            const flattened = {};
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    const newKey = prefix ? `${prefix}.${key}` : key;
                    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                        Object.assign(flattened, this.flattenObject(obj[key], newKey));
                    } else {
                        flattened[newKey] = obj[key];
                    }
                }
            }
            return flattened;
        },
        unflattenObject(flattened) {
            const unflattened = {};
            for (const key in flattened) {
                if (Object.prototype.hasOwnProperty.call(flattened, key)) {
                    const keys = key.split('.');
                    let current = unflattened;
                    for (let i = 0; i < keys.length - 1; i++) {
                        if (!current[keys[i]]) {
                            current[keys[i]] = {};
                        }
                        current = current[keys[i]];
                    }
                    current[keys[keys.length - 1]] = flattened[key];
                }
            }
            return unflattened;
        }
    }
};
</script>

<style scoped>
.spinner {
    border: 2px solid #f3f3f3;
    border-top: 2px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>
